interface GeolocationData {
  coords: {
    accuracy: number;
    latitude: number;
    longitude: number;
  };
  timestamp: number;
}

interface CachedLocationData {
  clientAccuracy?: number;
  clientLatitude?: number;
  clientLongitude?: number;
  timestamp: number;
}

const LOCATION_CACHE_KEY = 'geolocation_cache';

/**
 * The duration we allow previously cached geolocation results to be reused for, in ms, 5 minutes.
 */
const CACHE_DURATION_MS: number = 5 * 60 * 1000;

/**
 * The duration we wait for fresh geolocation data, in ms, 10 seconds. This should be a relatively
 * short value, since users can give up otherwise.
 */
const QUICK_TIMEOUT_MS: number = 10 * 1000;

/**
 * The maximum duration we wait for geolocation data to become available, in ms, 1 minute. Some
 * lenders have a strict requirement for geolocation data on all file uploads, for which we use
 * this large timeout.
 */
const FULL_TIMEOUT_MS: number = 60 * 1000;

class GeolocationManager {
  private static instance: GeolocationManager;
  private currentLocationRequest: Promise<GeolocationPosition> | null = null;

  private constructor() {}

  static getInstance(): GeolocationManager {
    if (!GeolocationManager.instance) {
      GeolocationManager.instance = new GeolocationManager();
    }
    return GeolocationManager.instance;
  }

  /**
   * Starts a geolocation request in background before the user has even captured the image. This
   * helps to reduce blocking in getLocationForUpload().
   */
  startBackgroundLocationRequest(): void {
    if (!window.navigator.geolocation) {
      return;
    }

    // Only start a new request if one isn't already in progress
    if (!this.currentLocationRequest) {
      this.currentLocationRequest = new Promise((resolve, reject) => {
        window.navigator.geolocation.getCurrentPosition(
          (position) => {
            // Cache the result
            this.cacheLocation(position);
            resolve(position);
          },
          (error) => {
            console.error(`Background geolocation error: ${error.message}`);
            reject(error);
          },
          {
            enableHighAccuracy: true,
            maximumAge: 0,
            timeout: FULL_TIMEOUT_MS
          }
        );
      });

      // Clear the request reference when it completes
      this.currentLocationRequest.finally(() => {
        this.currentLocationRequest = null;
      });
    }
  }

  /**
   * Get geolocation for file upload with intelligent caching and timeout logic
   */
  async getLocationForUpload(
    /* The inspection's user location sharing mode, eg "WARNING_PROMPT" */
    locationSharing: String | null
  ): Promise<CachedLocationData | null> {
    const cachedData = this.getCachedLocation();
    const now = Date.now();

    try {
      if (cachedData && now - cachedData.timestamp < CACHE_DURATION_MS) {
        // Cache is valid, try to get fresh data with timeout
        console.log('Using cached location with quick timeout for fresh data');

        try {
          const freshLocation =
            await this.getLocationWithTimeout(QUICK_TIMEOUT_MS);
          return this.formatLocationData(freshLocation);
        } catch (timeoutError) {
          // Timeout reached, use cached data
          console.log('Quick timeout reached, using cached location data');
          return {
            clientAccuracy: cachedData.clientAccuracy,
            clientLatitude: cachedData.clientLatitude,
            clientLongitude: cachedData.clientLongitude,
            timestamp: cachedData.timestamp
          };
        }
      } else {
        // No valid cache, wait indefinitely for location
        console.log('No valid cache, waiting for fresh location data');
        var timeoutMs: number = 0;

        // where location sharing isn't expected prioritise UX with a shorter maximum timeout.
        // Otherwise, users may become frustrated at the 60s long timeout and give up.
        if (locationSharing === 'SILENT') {
          timeoutMs = QUICK_TIMEOUT_MS;
        }

        const location = await this.getLocationWithTimeout(timeoutMs);
        return this.formatLocationData(location);
      }
    } catch (error) {
      console.error('Failed to get location for upload:', error);

      // If we have any cached data (even expired), use it as fallback
      if (cachedData) {
        console.log('Using expired cached location as fallback');
        return {
          clientAccuracy: cachedData.clientAccuracy,
          clientLatitude: cachedData.clientLatitude,
          clientLongitude: cachedData.clientLongitude,
          timestamp: cachedData.timestamp
        };
      }

      return null;
    }
  }

  /**
   * Get current location with optional timeout
   */
  private getLocationWithTimeout(
    timeoutMs: number
  ): Promise<GeolocationPosition> {
    if (!window.navigator.geolocation) {
      return Promise.reject(new Error('Geolocation not supported'));
    }

    // If we have a background request in progress, try to use it first
    if (this.currentLocationRequest) {
      if (timeoutMs > 0) {
        // Race between the existing request and timeout
        return Promise.race([
          this.currentLocationRequest,
          new Promise<never>((_, reject) => {
            setTimeout(() => reject(new Error('Timeout')), timeoutMs);
          })
        ]);
      } else {
        // No timeout, just wait for the existing request
        return this.currentLocationRequest;
      }
    }

    // Start new request
    return new Promise((resolve, reject) => {
      const options = {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: timeoutMs > 0 ? timeoutMs : FULL_TIMEOUT_MS
      };

      window.navigator.geolocation.getCurrentPosition(
        (position) => {
          this.cacheLocation(position);
          resolve(position);
        },
        reject,
        options
      );
    });
  }

  /**
   * Cache location data to localStorage
   */
  private cacheLocation(position: GeolocationPosition): void {
    const cacheData: CachedLocationData = {
      clientAccuracy: position.coords.accuracy,
      clientLatitude: position.coords.latitude,
      clientLongitude: position.coords.longitude,
      timestamp: Date.now()
    };

    try {
      localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Failed to cache location data:', error);
    }
  }

  /**
   * Get cached location data from localStorage
   */
  private getCachedLocation(): CachedLocationData | null {
    try {
      const cached = localStorage.getItem(LOCATION_CACHE_KEY);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
      console.error('Failed to retrieve cached location:', error);
    }
    return null;
  }

  /**
   * Format GeolocationPosition to our expected format
   */
  private formatLocationData(
    position: GeolocationPosition
  ): CachedLocationData {
    return {
      clientAccuracy: position.coords.accuracy,
      clientLatitude: position.coords.latitude,
      clientLongitude: position.coords.longitude,
      timestamp: Date.now()
    };
  }

  /**
   * Clear cached location data
   */
  clearCache(): void {
    try {
      localStorage.removeItem(LOCATION_CACHE_KEY);
    } catch (error) {
      console.error('Failed to clear location cache:', error);
    }
  }

  /**
   * Check if cached location is still valid
   */
  isCacheValid(): boolean {
    const cached = this.getCachedLocation();
    if (!cached) return false;

    const now = Date.now();
    return now - cached.timestamp < CACHE_DURATION_MS;
  }
}

export default GeolocationManager;
